import { AppTextService } from '../../shared/app-text';
import { FiDialogsService } from '../../shared/fi-dialogs';
import {
    Component,
    Input,
    OnInit,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    AfterViewInit,
    OnDestroy,
    ChangeDetectorRef,
    Output,
    EventEmitter,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FiFormSharedService } from '../../shared/fi-form-shared/fi-form-shared.service';
import { IFormReportView, IFormReportQuestion } from './fi-form-report';
import { takeUntil } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';
import { FiStateService } from '../../shared/fi-state-proxy/fi-state.proxy.service';
import { FormReportNavigation } from './fi-form-report-navigation';
import { isDefined } from '../../shared/utils';
import { TransitionOptions } from '@uirouter/angular';
import { FiStateNames } from '../../shared/fi-state-proxy/fi-state-names';
import { FiFormHelperService } from '../../shared/fi-form-shared/fi-form-helper.service';
import { FiFormService } from '@fi-app/fi-form/fi-form.service';
import { FiUserSettingType } from '@fi-shared/types/fi-user-setting-type';

@Component({
    selector: 'fi-form-report',
    templateUrl: './fi-form-report.component.html',
    styleUrls: ['./fi-form-report.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FiFormReportComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() formReport: IFormReportView;
    @Input() isCompleteReport: boolean = false;
    @Output() reportPosChange = new EventEmitter<string>();

    loading$ = new BehaviorSubject<boolean>(false);
    reportEndDate = new Date();
    reportStartDate: Date;

    formReportNavigation = {
        showReportActivity: false,
        showActiveColleagues: false,
        showInactiveColleagues: false,
        showQuestionDetails: false,
    };

    selectedQuestion: IFormReportQuestion;

    constructor(
        private formSharedService: FiFormSharedService,
        private formService: FiFormService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
        private cdr: ChangeDetectorRef,
        private fiFormHelperService: FiFormHelperService,
    ) {}

    ngOnInit() {
        this.reportStartDate = new Date(this.formReport.CreatedOn);

        this.showOnboardingDialog();

        if (isDefined(this.getCurrentPos())) {
            this.stateService.setBackButtonHandler(() => this.updatePos());
        }
        if (this.getCurrentPos() === FormReportNavigation.QuestionDetails) {
            this.navigateToState(undefined, {
                location: 'replace',
            });
        }
        setTimeout(() => {
            this.updatePos();
        });
    }

    isPlainViewMode$(): Observable<boolean> {
        return this.fiFormHelperService.plainViewMode$;
    }

    showOnboardingDialog() {
        this.fiDialogsService.onboarding({
            headerText: this.appTextService.get('form.report_onboarding_heading'),
            yesButtonText: this.appTextService.get('common.got_it'),
            messageText: this.appTextService.get('form.report_onboarding_content_message'),
            dontShowAgainProperty: FiUserSettingType.DontShowFormReportOnboardingMessage,
            config: {
                width: '500px',
            },
        });
    }

    onEndDateChange($event) {
        this.reportEndDate = $event.value;
        this.reloadFormReport();
    }

    protected reloadFormReport() {
        this.loading$.next(true);
        this.formService
            .getFormReport(this.formReport.Id, this.reportStartDate.toISOString(), this.reportEndDate.toISOString())
            .then((formReportData) => {
                this.formReport.Summary = formReportData.Summary;
                this.formReport.Questions = formReportData.Questions;
                this.formReport.Conditions = formReportData.Conditions;
                this.formReport.Submissions = formReportData.Submissions;
                this.formReport.ActiveColleagues = formReportData.ActiveColleagues;
                this.formReport.InactiveColleagues = formReportData.InactiveColleagues;
                this.loading$.next(false);
            });
    }

    onBackEmit() {
        this.updatePos();
    }

    isMainCard() {
        return Object.keys(this.formReportNavigation).every((prop) => this.formReportNavigation[prop] === false);
    }

    navigateToSubmissions() {
        this.navigateToState(FormReportNavigation.Submissions);
    }

    navigateToActiveColleagues() {
        this.navigateToState(FormReportNavigation.ActiveColleagues);
    }

    navigateToInactiveColleagues() {
        this.navigateToState(FormReportNavigation.InactiveColleagues);
    }

    navigateToQuestionDetails(event: IFormReportQuestion) {
        this.selectedQuestion = event;
        this.navigateToState(FormReportNavigation.QuestionDetails);
    }

    navigateToSubmissionDetails() {
        this.navigateToSubmissions();
    }

    ngAfterViewInit(): void {
        const _timer = timer(4000);
        _timer.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            const answerSetIds = [];
            this.formReport.Submissions.forEach((submission) => {
                submission.AnswerSets.forEach((answerSet) => answerSetIds.push(answerSet.Id));
            });
            this.formSharedService.markFormAnswerSetsAsVisited(answerSetIds).then(() => {
                this.formService.onFormAnswerSetsAsVisited(answerSetIds);
            });
        });
    }

    private navigateToState(newPos, options?: TransitionOptions) {
        this.stateService.go(
            FiStateNames.Form.Report.View,
            {
                id: this.formReport.Id,
                pos: newPos,
            },
            options,
        );

        this.stateService.setBackButtonHandler(() => this.updatePos());
        setTimeout(() => {
            this.updatePos();
        });
    }

    private updatePos() {
        Object.keys(this.formReportNavigation).forEach((prop) => (this.formReportNavigation[prop] = false));
        const currentPos = this.getCurrentPos();
        if (currentPos === FormReportNavigation.Submissions) {
            this.formReportNavigation.showReportActivity = true;
        } else if (currentPos === FormReportNavigation.ActiveColleagues) {
            this.formReportNavigation.showActiveColleagues = true;
        } else if (currentPos === FormReportNavigation.InactiveColleagues) {
            this.formReportNavigation.showInactiveColleagues = true;
        } else if (currentPos === FormReportNavigation.QuestionDetails) {
            this.formReportNavigation.showQuestionDetails = true;
        }
        this.reportPosChange.next(currentPos);
        this.cdr.markForCheck();
    }

    private getCurrentPos() {
        return this.stateService.params.pos;
    }

    showColleaguesSummary() {
        return !this.fiFormHelperService.isAnonymousAnswers(this.formReport);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
